import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

export type AssistantSession = Schema['AssistantSessionDto']
export type ChatMessage = Schema['ChatMessageDto']
export type AssistantReply = Schema['AssistantReplyDto']
export type AssistantContext = Schema['AssistantContextDto']

/**
 * The four SSE payloads, hand-written — the only hand-written types the assistant needs.
 *
 * Everything else it touches is generated, because it is the return type of some operation.
 * These four are not: they are the bodies of `token`, `tool`, `done` and `error` events on
 * `POST /assistant/messages/stream`, and a `text/event-stream` response has no schema in
 * OpenAPI to describe what it frames.
 *
 * Transcribed from `CampaignManager.Api/Contracts/Ai/AssistantContracts.cs`. They are small
 * and the stream test asserts on all of them, which is what would notice them drifting.
 */

/** One chunk of a streamed reply. Event `token`. */
export interface AssistantTokenEvent {
  text: string
}

/**
 * A tool the assistant ran mid-answer. Event `tool`.
 *
 * Not part of the transcript and never persisted — it exists to fill the seconds a tool takes,
 * because reading a sheet or drafting a character is slow enough that silence reads as a hang. The
 * one durable thing a tool produces is a draft, and that arrives on the reply itself.
 */
export interface AssistantToolEvent {
  /** The tool's name, e.g. `draft_character`. */
  tool: string
  /** The draft it produced, when it produced one. */
  draftId: string | null
  /** Whether it objected. The assistant will usually correct itself and try again. */
  isError: boolean
}

/** The terminal event of a streamed reply: both persisted turns. Event `done`. */
export interface AssistantDoneEvent {
  userMessage: ChatMessage
  reply: ChatMessage
}

/**
 * The terminal event when a streamed reply fails. Event `error`.
 *
 * Worth knowing what it implies: **nothing was persisted for the assistant turn**. The
 * user's message was, so a retry does not need to resend it — but the transcript will show
 * a question with no answer until one is asked again.
 */
export interface AssistantErrorEvent {
  message: string
}

/** A message being streamed, before the server has persisted it. */
export interface PendingReply {
  /** The question, echoed locally so the transcript reads right while the answer arrives. */
  question: string
  /** Tokens so far, concatenated. */
  text: string
  /**
   * The tool running right now, if one is. Only the latest is kept: this is a status line, not a
   * log, and a list of everything the assistant has done is not what somebody waiting wants to read.
   */
  tool: string | null
}

/**
 * Human labels for the tools, for the status line while one runs.
 *
 * Falls back to the raw name for anything unmapped — a tool added later should read a little
 * awkwardly rather than making the line disappear.
 */
export const TOOL_LABELS: Record<string, string> = {
  list_skills: 'Checking the skill list',
  list_ability_templates: 'Reading the ability library',
  get_rules_reference: 'Checking the rules',
  find_characters: 'Looking through your characters',
  get_character_sheet: 'Reading the character sheet',
  draft_character: 'Building a character',
  draft_skills: 'Choosing skills',
  draft_ability: 'Working out an ability',
  draft_rote_spells: 'Working out spells',
}

/** What to show while a tool runs. */
export function describeTool(tool: string): string {
  return TOOL_LABELS[tool] ?? tool.replace(/_/g, ' ')
}
